import type { Node } from "@xyflow/react";

/**
 * Kind → rank mapping used by the L2 layout.
 *
 * Containers are grouped into four horizontal rows, top-to-bottom:
 *
 *   Rank 0 — edge / entry points:   frontend, gateway
 *   Rank 1 — application logic:     service, api, backend
 *   Rank 2 — async / background:    worker, job, queue
 *   Rank 3 — data + cache:          database, cache, storage, ml-model
 *
 * If a container's kind doesn't match any bucket (or is missing entirely),
 * it falls back to rank 1 — the services row — which is the safest default
 * for unknown types and keeps it in the middle of the diagram rather than
 * stranded at top or bottom.
 *
 * When adding a new kind to ContainerKind / ContainerType, decide which
 * row it belongs in and add it here. The four buckets are deliberately
 * coarse — resist adding a fifth row unless the canvas reads worse with
 * the new kind folded into an existing one.
 */
export const KIND_RANK_MAP: Record<string, 0 | 1 | 2 | 3> = {
  // Rank 0 — edge
  frontend: 0,
  gateway: 0,
  // Rank 1 — services
  service: 1,
  api: 1,
  backend: 1,
  // Rank 2 — async
  worker: 2,
  job: 2,
  queue: 2,
  // Rank 3 — data
  database: 3,
  cache: 3,
  storage: 3,
  "ml-model": 3,
};

const FALLBACK_RANK: 0 | 1 | 2 | 3 = 1;

export function rankForKind(kind: string | undefined): 0 | 1 | 2 | 3 {
  if (!kind) return FALLBACK_RANK;
  return KIND_RANK_MAP[kind] ?? FALLBACK_RANK;
}

/**
 * Per-rank spacing. Dagre uses a single global nodesep/ranksep; here we
 * vary them so a sparse row (1–2 nodes) is centered tightly while a dense
 * row breathes. ranksep is the gap BETWEEN the row and the next; nodesep
 * is the gap between sibling nodes in the same row.
 */
const RANK_NODESEP: Record<number, number> = {
  0: 80, // frontends are usually few — a bit of breathing room
  1: 56, // services pack denser
  2: 60, // workers
  3: 72, // datastores get a touch more so disk icons don't crowd
};

const ROW_GAP = 90; // vertical gap between rank rows
const TOP_PADDING = 0;
// Matches CONTAINER_NODE_SIZE in use-graph-elements.ts. Width = the
// ContainerNode's max-w cap (280); height = worst-case content height
// (~125px with description + tech-chip row), rounded up to 140 with
// headroom. Without these bumps, ReactFlow's overflow:visible wrapper
// let long-labelled nodes overflow on the right/bottom and the cluster
// sub-boundaries computed in buildL2 fell INSIDE the visible content.
const NODE_WIDTH = 280;
const NODE_HEIGHT = 140;

interface LaidOutContainer {
  id: string;
  rank: 0 | 1 | 2 | 3;
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * Layout containers into rows by rank. Each row is centered horizontally
 * around x=0 so sparse rows align with dense rows on the same axis.
 */
export function layoutContainersByRank(
  containers: ReadonlyArray<{ id: string; kind: string | undefined }>,
): {
  placed: LaidOutContainer[];
  bbox: { minX: number; minY: number; maxX: number; maxY: number };
} {
  // Group containers by rank
  const byRank: Record<number, Array<{ id: string }>> = { 0: [], 1: [], 2: [], 3: [] };
  for (const c of containers) {
    byRank[rankForKind(c.kind)].push({ id: c.id });
  }

  const placed: LaidOutContainer[] = [];
  let currentY = TOP_PADDING;
  let minX = 0;
  let maxX = 0;

  for (const rank of [0, 1, 2, 3] as const) {
    const row = byRank[rank];
    if (row.length === 0) continue;

    const nodesep = RANK_NODESEP[rank] ?? 60;
    const rowWidth = row.length * NODE_WIDTH + (row.length - 1) * nodesep;
    const rowStartX = -rowWidth / 2;

    for (let i = 0; i < row.length; i++) {
      const c = row[i];
      if (!c) continue;
      const x = rowStartX + i * (NODE_WIDTH + nodesep);
      placed.push({
        id: c.id,
        rank,
        x,
        y: currentY,
        w: NODE_WIDTH,
        h: NODE_HEIGHT,
      });
      if (x < minX) minX = x;
      if (x + NODE_WIDTH > maxX) maxX = x + NODE_WIDTH;
    }

    currentY += NODE_HEIGHT + ROW_GAP;
  }

  const maxY = currentY - ROW_GAP; // last row's bottom
  return { placed, bbox: { minX, minY: TOP_PADDING, maxX, maxY } };
}

/**
 * Place externals around the perimeter of the container frame.
 *
 * **All externals consolidate to the RIGHT side** as a single vertical
 * column. This matches the Wolf DMS C4 prototype's L2 — a dotted EXTERNAL
 * SERVICES cluster wraps one tight column rather than three separate
 * rects sprayed around the frame. The earlier kind-aware split
 * (auth-provider→left, saas→right, data-source→bottom) was a fix for
 * actor overlap, not a feature the user could read; with the actor row
 * already pinned above the frame, the constraint on perimeter sides goes
 * away.
 *
 * Within the right column, externals are sorted top→bottom by their
 * **primary connected rank** — the dominant rank among the containers
 * they touch. So auth-providers (rank 1 services) end up near the top,
 * saas (rank 1 services) middle, data-source (rank 2 workers / rank 3
 * data) at the bottom. Same-rank externals fall back to id-stable order.
 *
 * TOP is still reserved for the actor row; nothing here writes to top.
 */
interface ExternalPlacement {
  id: string;
  x: number;
  y: number;
}

interface ExternalInput {
  id: string;
  kind: string | undefined;
  connectedContainerIds: ReadonlyArray<string>;
}

// Matches EXTERNAL_NODE_SIZE_EXPANDED.height in use-graph-elements.ts —
// 200 is the worst-case rendered height of a non-compact ExternalSystemNode
// (icon + 2-line label + headline + 4-line description + footer with
// border/padding; see wolf-dms microsoft-purview / microsoft-graph). Layout
// must use the same value or the vertical stacking under-spaces and
// externals overlap their neighbours (bumped from 140 in gap #6/9.2).
const EXTERNAL_NODE_HEIGHT = 200;
// Distance from the BUILD frame's outer wall to the external column.
// Must be > systemFrame's outer PAD (60) + cluster sub-boundary inner
// padding (20) + a visible gap (>=32px per gap #6/2.2) so the EXTERNAL
// SERVICES sub-boundary doesn't crowd the BUILD frame's right edge.
const PERIMETER_GAP = 120;
// Gap between stacked externals on the same side. Bumped from 24 to 32
// in gap #6/9.2 so adjacent externals show clear breathing room — at 24
// they read as touching when their declared heights are filled.
const PERIMETER_STACK_GAP = 32;

/**
 * Dominant rank among an external's connected containers. Used as the
 * sort key for the right-column ordering — auth-providers (services-heavy)
 * end up near the top, data-sources (workers/data-heavy) near the bottom.
 * Ties prefer the lower rank (closer to top). Externals with no recorded
 * connections sort to rank 1 (services row) as a neutral default.
 */
function primaryRankForExternal(
  ext: ExternalInput,
  containerRankById: ReadonlyMap<string, 0 | 1 | 2 | 3>,
): 0 | 1 | 2 | 3 {
  if (ext.connectedContainerIds.length === 0) return 1;
  const votes: Record<number, number> = { 0: 0, 1: 0, 2: 0, 3: 0 };
  for (const cid of ext.connectedContainerIds) {
    const r = containerRankById.get(cid);
    if (r !== undefined) votes[r]++;
  }
  let bestRank: 0 | 1 | 2 | 3 = 1;
  let bestVotes = -1;
  for (const r of [0, 1, 2, 3] as const) {
    const v = votes[r] ?? 0;
    if (v > bestVotes) {
      bestVotes = v;
      bestRank = r;
    }
  }
  return bestRank;
}

export function layoutExternalsAroundPerimeter(
  externals: ReadonlyArray<ExternalInput>,
  containerRankById: ReadonlyMap<string, 0 | 1 | 2 | 3>,
  containerBBox: { minX: number; minY: number; maxX: number; maxY: number },
): ExternalPlacement[] {
  if (externals.length === 0) return [];

  // Sort the right column top→bottom by primary connected rank, then by id
  // for a stable ordering when ranks tie. Kind hints no longer influence
  // side assignment — sortKey is the only ordering signal.
  const sorted = [...externals].sort((a, b) => {
    const ra = primaryRankForExternal(a, containerRankById);
    const rb = primaryRankForExternal(b, containerRankById);
    if (ra !== rb) return ra - rb;
    return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
  });

  const centerY = (containerBBox.minY + containerBBox.maxY) / 2;
  const totalH = sorted.length * EXTERNAL_NODE_HEIGHT + (sorted.length - 1) * PERIMETER_STACK_GAP;
  const startY = centerY - totalH / 2;
  const x = containerBBox.maxX + PERIMETER_GAP;

  return sorted.map((ext, i) => ({
    id: ext.id,
    x,
    y: startY + i * (EXTERNAL_NODE_HEIGHT + PERIMETER_STACK_GAP),
  }));
}

export function applyContainerPositions(nodes: Node[], placed: LaidOutContainer[]): Node[] {
  const byId = new Map(placed.map((p) => [p.id, p]));
  return nodes.map((n) => {
    const p = byId.get(n.id);
    if (!p) return n;
    return { ...n, position: { x: p.x, y: p.y } };
  });
}

export function applyExternalPositions(nodes: Node[], placed: ExternalPlacement[]): Node[] {
  const byId = new Map(placed.map((p) => [p.id, p]));
  return nodes.map((n) => {
    const p = byId.get(n.id);
    if (!p) return n;
    return { ...n, position: { x: p.x, y: p.y } };
  });
}
